import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Button,
  Dimensions,
  InputAccessoryView,
  Platform,
  View,
  Keyboard,
  StyleSheet,
} from 'react-native';
import { TextInputFormProps } from './types';
import { hasRequiredFields } from './validation';
import { useTheme, Text } from 'react-native-paper';
import { Border, Color } from '@/GlobalStyles';
import TextInput from '@/components/CustomRNPaper/TextInput';
import { TextInput as NativeTextInput } from 'react-native/Libraries/Components/TextInput/TextInput';
import { useTranslation } from 'react-i18next';
import HelpButton from '@/components/HelpButton';

const FTextInput = React.forwardRef<NativeTextInput, TextInputFormProps>(
  (props: TextInputFormProps, ref): React.ReactElement => {
    const theme = useTheme();
    const {
      label,
      labelStyle,
      error,
      onChangeValue,
      onChangeText,
      style,
      defaultValue,
      containerStyle,
      secureTextEntry,
      keyboardType,
      validators,
      placeholder,
      showAsterix = true,
      inputAccessoryViewID = 'Terminer',
      clearStateFunc,
      withMargin = true,
      withError = true,
      withHelpButton = false,
      helpFieldName,
      topLabel = false,
      name,
      ...inputProps
    } = props;

    let realDefaultValue: string | number | undefined =
      inputProps.value !== undefined ? inputProps.value : defaultValue;

    const [inputValue, setInputValue] = useState<string | number | undefined>(realDefaultValue);

    const { t } = useTranslation();

    if (clearStateFunc) {
      clearStateFunc(() => {
        setInputValue('');
      });
    }

    useEffect(() => {
      setInputValue(realDefaultValue);
    }, [defaultValue, inputProps.value]);

    const required =
      showAsterix && validators && hasRequiredFields(validators) ? (
        <Text style={{ color: theme.colors.secondary }}> *</Text>
      ) : null;

    // Create the label with help button if needed
    const labelWithHelp = withHelpButton && (helpFieldName || name) ? (
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text>
          {t(label)}
          {required}
        </Text>
        <HelpButton 
          fieldName={helpFieldName || name || ''} 
          size={16}
          style={{ marginLeft: 4 }}
        />
      </View>
    ) : (
      required !== null ? (
        <>
          {t(label)}
          {required}
        </>
      ) : (
        t(label)
      )
    );

    return (
      <>
        <View
          style={[
            styles.container,
            topLabel ? styles.containerTopLabel : null,
            withMargin
              ? {
                  marginBottom: 10,
                }
              : null,
            containerStyle,
          ]}
        >
          {topLabel && label ? (
            <View style={styles.topLabelContainer}>
              <Text variant={'labelMedium'} style={styles.topLabelText} numberOfLines={2}>
                {t(label)}
                {required}
              </Text>
              {withHelpButton && (helpFieldName || name) ? (
                <HelpButton
                  fieldName={helpFieldName || name || ''}
                  size={16}
                  style={{ marginLeft: 4 }}
                />
              ) : null}
            </View>
          ) : null}
          <TextInput
            autoCapitalize="none"
            ref={ref}
            label={topLabel ? undefined : labelWithHelp}
            keyboardType={keyboardType || 'default'}
            inputAccessoryViewID={inputAccessoryViewID}
            placeholder={(t(placeholder) || '') + (!label && required !== null ? required : '')}
            style={[styles.input, style]}
            errorMessage={withError ? error?.message : undefined}
            error={error?.message !== undefined}
            {...inputProps}
            secureTextEntry={secureTextEntry}
            onChangeText={(text) => {
              let finalText = text;
              if (keyboardType === 'numeric' || keyboardType === 'decimal-pad') {
                finalText = text.replace(',', '.');
              }
              setInputValue(finalText);
              if (onChangeValue) {
                onChangeValue(finalText);
              }
            }}
            theme={{
              roundness: 24,
            }}
            value={inputValue?.toString()}
            mode="flat"
            dense={true}
            underlineColor={'transparent'}
            activeUnderlineColor={theme.colors.primary}
            underlineStyle={{ height: 0 }}
          />
        </View>
        {Platform.OS === 'ios' && keyboardType === 'numeric' && (
          <InputAccessoryView nativeID={inputAccessoryViewID}>
            <View style={styles.accessory}>
              <Button onPress={Keyboard.dismiss} title="Terminer" />
            </View>
          </InputAccessoryView>
        )}
      </>
    );
  }
);

FTextInput.displayName = 'TextInput';

export default FTextInput;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    marginTop: 21,
  },
  containerTopLabel: {
    marginTop: 0,
  },
  topLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    flexWrap: 'nowrap',
  },
  topLabelText: {
    flex: 1,
    flexShrink: 1,
    flexWrap: 'wrap',
  },
  input: {
    height: 48,
    borderWidth: 1,
    borderColor: Color.colorGray_100,
    borderStyle: 'solid',
    borderRadius: Border.br_3xs,
    backgroundColor: Color.blanc,
  },
  accessory: {
    width: Dimensions.get('window').width,
    height: 48,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    backgroundColor: '#F8F8F8',
    paddingHorizontal: 8,
  },
});
